import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';

// Recharts can't read Tailwind's dark class, so derive chart colors from the
// active theme and pass them in explicitly. Keeps the chart legible in both modes.
const getChartColors = (theme) => {
    const isDark = theme === 'dark';
    return {
        grid: isDark ? '#374151' : '#E5E7EB',   // gray-700 / gray-200
        axis: isDark ? '#9CA3AF' : '#6B7280',   // gray-400 / gray-500
        cursor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        tooltip: {
            backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
            borderColor: isDark ? '#374151' : '#E5E7EB',
            color: isDark ? '#F3F4F6' : '#111827',
            borderRadius: '0.5rem',
        },
    };
};

/**
 * @file AnalyticsDashboard.js
 * @description A component to display analytics about gift card purchases and savings.
 */

// A sub-component to render the table showing best discounts
const BestDiscountsTable = ({ data }) => (
    <div>
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-3">Best Discounts by Source</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700">
            <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
                <thead className="text-xs text-gray-700 dark:text-gray-200 uppercase bg-gray-100 dark:bg-slate-700">
                    <tr>
                        <th scope="col" className="px-3 py-2 whitespace-nowrap">Source</th>
                        <th scope="col" className="px-3 py-2 text-right whitespace-nowrap">Avg. Discount</th>
                        <th scope="col" className="px-3 py-2 text-right whitespace-nowrap">Total Saved</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item) => (
                        <tr key={item.source} className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700">
                            <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap">{item.source || 'N/A'}</td>
                            <td className="px-3 py-2 text-right font-semibold text-green-700 dark:text-green-400 whitespace-nowrap">{item.avgDiscount.toFixed(2)}%</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">${item.totalSavings.toFixed(2)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

// A sub-component to render the bar chart for visual comparison
const SpendingChart = ({ data }) => {
    const { theme } = useTheme();
    const colors = getChartColors(theme);
    return (
        <div>
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-3">Value vs. Paid by Source</h3>
            <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                    <BarChart data={data} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.grid} />
                        <XAxis dataKey="source" tick={{ fontSize: 12, fill: colors.axis }} stroke={colors.axis} />
                        <YAxis tickFormatter={(value) => `$${value}`} tick={{ fontSize: 12, fill: colors.axis }} stroke={colors.axis} />
                        <Tooltip
                            formatter={(value) => `$${Number(value).toFixed(2)}`}
                            cursor={{ fill: colors.cursor }}
                            contentStyle={colors.tooltip}
                        />
                        <Legend wrapperStyle={{ fontSize: '14px', color: colors.axis }} />
                        <Bar dataKey="totalValue" fill="#15803d" name="Total Value" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="totalPaid" fill="#65a30d" name="Total Paid" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};


// The main AnalyticsDashboard component
export default function AnalyticsDashboard({ cards }) {
    // We use useMemo to avoid re-calculating on every render,
    // only when the gift cards data changes.
    const analyticsData = useMemo(() => {
        if (!cards || cards.length === 0) {
            return [];
        }

        // Group cards by source to aggregate data
        const sourceGroups = cards.reduce((acc, card) => {
            const source = card.source || 'Unknown'; // Handle cases where source might be empty
            if (!acc[source]) {
                acc[source] = {
                    totalValue: 0,
                    totalPaid: 0,
                    cardCount: 0,
                };
            }
            acc[source].totalValue += card.initialValue;
            acc[source].totalPaid += card.pricePaid;
            acc[source].cardCount += 1;
            return acc;
        }, {});

        // Transform the grouped data into an array and calculate stats
        const sourceStats = Object.keys(sourceGroups).map(source => {
            const group = sourceGroups[source];
            const totalValue = group.totalValue;
            const totalPaid = group.totalPaid;
            const totalSavings = totalValue - totalPaid;
            // Calculate average discount, avoiding division by zero
            const avgDiscount = totalValue > 0 ? (totalSavings / totalValue) * 100 : 0;

            return {
                source,
                totalValue,
                totalPaid,
                totalSavings,
                avgDiscount,
            };
        });

        // Sort by the highest average discount first
        return sourceStats.sort((a, b) => b.avgDiscount - a.avgDiscount);

    }, [cards]); // Dependency array: recalculate only when cards change

    // Don't render the component if there's no data to show
    if (analyticsData.length === 0) {
        return null;
    }

    return (
        <div className="mt-8 bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-gray-700 dark:text-white mb-6 border-b dark:border-slate-700 pb-4">
                Analytics Dashboard
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start [&>*]:min-w-0">
                <BestDiscountsTable data={analyticsData} />
                <SpendingChart data={analyticsData} />
            </div>
        </div>
    );
}
